import React, { useEffect, useState } from 'react';
import { apiBase } from '../lib/api';
import { formatCurrency, timeAgo } from '../donor/format';

interface RecentDonation {
  readonly amount: number;
  readonly currency: string;
  readonly donorName: string | null;
  readonly createdAt: string;
}

/**
 * Live log of recent completed gifts, sourced from the portal backend
 * (GET /api/donations/recent) — never invented client-side. Fetched once on mount, matching
 * ProjectOne/useDonationsAvailable: nobody sits on the homepage waiting for a new donation to
 * appear, so a poll loop would just be traffic. `donorName` already arrives redacted server-side
 * for an anonymous gift, so this component never has to know which anonymity level made it null.
 */
const RecentDonations: React.FC = () => {
  const [donations, setDonations] = useState<readonly RecentDonation[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${apiBase}/api/donations/recent?limit=8`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: { donations: RecentDonation[] }) => setDonations(data.donations))
      .catch((e) => { if (e.name !== 'AbortError') setFailed(true); });
    return () => controller.abort();
  }, []);

  return (
    <div className="card p-6 max-w-md w-full">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-bold text-gray-800">Recent Gifts</h4>
        <span className="text-xs font-medium text-gray-400">
          {failed ? 'Feed unavailable' : donations ? 'Live' : 'Loading…'}
        </span>
      </div>
      {failed && (
        <p className="text-sm text-gray-500">Recent gifts aren&apos;t available right now.</p>
      )}
      {!failed && donations && donations.length === 0 && (
        <p className="text-sm text-gray-500">No gifts yet — be the first!</p>
      )}
      {!failed && donations && donations.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {donations.map((d, i) => (
            <li key={`${d.createdAt}-${i}`} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
              <span className="text-gray-800 font-medium truncate">{d.donorName ?? 'Anonymous'}</span>
              <span className="flex items-baseline gap-2 shrink-0">
                <span className="font-bold text-gray-800 tabular-nums">{formatCurrency(d.amount, d.currency)}</span>
                <span className="text-xs text-gray-400">{timeAgo(d.createdAt)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RecentDonations;
